import React, { useMemo } from 'react';
import { Table } from 'src/components/shared/table/table';
import { NoResult } from 'src/components/shared/no-result/no-result';
import { Column } from '../../../../../../model/table';
import { AllReportsFieldsConfig } from '../../../report-widget-factory/all-top10-columns-config';
import { ReportName, TableMode } from 'src/utils/enums';
import { top10SourceDeniedColumns } from '../../../report-widget-factory/top-source-denied-report-config';
import { top10SourceAllowedColumns } from '../../../report-widget-factory/top-source-allowed-report-config';
import { topTalkersColumns } from '../../../report-widget-factory/top-talkers-config';
import { topDestinationAllowedColumns } from '../../../report-widget-factory/top-destinations-allowed-config';
import { topDestinationDeniedColumns } from '../../../report-widget-factory/top-destinations-denied-config';
import { topDetectedThreatsReportConfig } from '../../../report-widget-factory/top-detected-threats-report-config';
import { topCountryBlockedHits } from '../../../report-widget-factory/top-country-blocked-hits';
import { topListHits } from '../../../report-widget-factory/top-list-hits-config';
import { topBlockedOrAllowedUrls } from '../../../report-widget-factory/top-urls-config';
import { topUrlFilteringCategoriesAndDns } from '../../../report-widget-factory/top-url-filtering-categories-and-dns-config';
import { topBlockedDomains } from '../../../report-widget-factory/top-blocked-domains';
import { topBlockDnsCategories } from '../../../report-widget-factory/top-block-dns-categories-config';
import { topDDShieldHitsConfig } from '../../../report-widget-factory/top-dd-shield-hits-config';
import {
    topBlockedAppsReportConfig
} from '@src/components/features/reports-and-logs/report-widget-factory/top-blocked-apps-report-config';
import {
    topBlockedServicesReportConfig
} from '@src/components/features/reports-and-logs/report-widget-factory/top-blocked-services-report-config';
import {
    top15DetectedThreatsReportConfig
} from '@src/components/features/reports-and-logs/report-widget-factory/top-15-detected-threats-report-config';
import {
    topBlockedCountriesWithPercentReportConfig
} from '@src/components/features/reports-and-logs/report-widget-factory/top-blocked-countries-with-percent-report-config';

interface GenericReportProps {
    data: Partial<AllReportsFieldsConfig>[];
    reportName: ReportName;
}

// thw width percentages need to be max 95% - the rest is for position column
const ReportsColumnsConfig: Partial<Record<ReportName, Column<any>[]>> = {
    [ReportName.TOP_SOURCSE_DENIED]: top10SourceDeniedColumns,
    [ReportName.TOP_SOURCSE_ALLOWED]: top10SourceAllowedColumns,
    [ReportName.TOP_TALKERS]: topTalkersColumns,
    [ReportName.TOP_DESTINATION_ALLOWED]: topDestinationAllowedColumns,
    [ReportName.TOP_DESTINATION_DENIED]: topDestinationDeniedColumns,
    [ReportName.TOP_URL_FILTERING_CATEGORIES_DENIED]: topUrlFilteringCategoriesAndDns,
    [ReportName.TOP_URL_FILTERING_CATEGORIES_ALLOWED]: topUrlFilteringCategoriesAndDns,
    [ReportName.TOP_BLOCKED_URL]: topBlockedOrAllowedUrls,
    [ReportName.TOP_ALLOWED_URL]: topBlockedOrAllowedUrls,
    [ReportName.TOP_DNS_CATEGORIES_BLOCKED]: topBlockDnsCategories,
    [ReportName.TOP_BLOCKED_DOMAINS]: topBlockedDomains,  
    [ReportName.TOP_ALLOWED_LIST_HITS]: topListHits,
    [ReportName.TOP_BLOCKED_LIST_HITS]: topListHits,
    [ReportName.TOP_GLOBAL_BLOCK_OUTBOUND_HITS]: topDDShieldHitsConfig,
    [ReportName.TOP_BLOCKED_COUNTRIES_INBOUND]: topCountryBlockedHits,
    [ReportName.TOP_BLOCKED_COUNTRIES_OUTBOUND]: topCountryBlockedHits,
    [ReportName.TOP_DETECTED_THREATS]: topDetectedThreatsReportConfig,
    [ReportName.TOP_15_BLOCKED_APPS]: topBlockedAppsReportConfig,
    [ReportName.TOP_15_BLOCKED_SERVICES]: topBlockedServicesReportConfig,
    [ReportName.TOP_15_ALLOWED_COUNTRIES]: topCountryBlockedHits,
    [ReportName.TOP_15_BLOCKED_COUNTRIES]: topBlockedCountriesWithPercentReportConfig,
    [ReportName.TOP_15_DETECTED_THREATS]: top15DetectedThreatsReportConfig,
};

const getMaxValueOf = <T, K extends keyof T>(items: T[], property: K): number =>
    Math.max(...items.map(item => item[property] as number ?? 0));

const createDataWithIndexAndMaxValues = (data: Partial<AllReportsFieldsConfig>[] = []) => {
    const maxValues = {
        maxCount: getMaxValueOf(data, 'count'),
        maxSessions: getMaxValueOf(data, 'sessions'),
        maxBytes: getMaxValueOf(data, 'bytes'),
    };

    return data.map((row, index) => ({
        ...row,
        index: index + 1,
        ...maxValues
    }));
};

const GenericReport: React.FC<GenericReportProps> = ({ data, reportName }) => {

    const reportsColumns = useMemo(() =>
        ReportsColumnsConfig[reportName], [reportName, data]);

    const processedData = useMemo(() => createDataWithIndexAndMaxValues(data), [data]);

    return reportsColumns?.length && processedData.length ? (
        <Table mode={TableMode.LIGHT} columnsConfig={reportsColumns}
        content={processedData}/>
    ) : <NoResult/>;
};

export default GenericReport;
